import { pushSubscriptionInputSchema } from '@orbit-sync/shared-types';
import { api } from '../lib/api/index.js';
import { serviceWorkerRegistration } from '../pwa/serviceWorker.js';

/**
 * Web Push for this browser: what lets a phone show "new message" while
 * Orbit Sync is closed or suspended, which is when the socket is gone.
 *
 * Needs a granted notification permission, a service worker, and a server with
 * push configured. Every step degrades to `false`, and the in-page
 * notifications carry on as before.
 */

/** base64url -> bytes, for `applicationServerKey`. */
function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));

  for (let index = 0; index < raw.length; index += 1) {
    bytes[index] = raw.charCodeAt(index);
  }

  return bytes;
}

function sameKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const current = subscription.options.applicationServerKey;

  if (!current) {
    return false;
  }

  const bytes = new Uint8Array(current);
  return bytes.length === key.length && bytes.every((value, index) => value === key[index]);
}

/**
 * Makes sure this browser is subscribed and the server knows it - for the
 * signed-in user and session. Idempotent: run it on every start, since the
 * server may have lost the row or another account may have used this browser.
 * Resolves to whether push is active.
 */
export async function syncPushSubscription(): Promise<boolean> {
  try {
    const registration = await serviceWorkerRegistration();

    if (!registration || !('pushManager' in registration)) {
      return false;
    }

    const { publicKey } = await api.push.getKey();

    if (!publicKey) {
      return false;
    }

    const key = keyBytes(publicKey);
    let subscription = await registration.pushManager.getSubscription();

    // Made for another server key (a reinstalled server): it would never work.
    if (subscription && !sameKey(subscription, key)) {
      await subscription.unsubscribe();
      subscription = null;
    }

    subscription ??= await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    });

    await api.push.subscribe(pushSubscriptionInputSchema.parse(subscription.toJSON()));
    return true;
  } catch (error) {
    console.warn('Push subscription unavailable', error);
    return false;
  }
}

/** Stops pushes to this browser - on the server first, while still signed in. */
export async function removePushSubscription(): Promise<void> {
  try {
    const registration = await serviceWorkerRegistration();
    const subscription = await registration?.pushManager.getSubscription();

    if (!subscription) {
      return;
    }

    await api.push.unsubscribe(subscription.endpoint).catch(() => undefined);
    await subscription.unsubscribe();
  } catch {
    // Nothing more to do; the server drops a dead subscription on its own.
  }
}
